/**
 * ProfileIcon component
 * Displays a circular profile image with fallback to default student avatar
 * Follows the AppLogo error handling pattern for image load failures
 */

import { useState, useEffect } from 'react';
import { cn } from '@/lib/utils';
import studentAvatar from '@/assets/student-avatar.png';

export interface ProfileIconProps {
  /** S3 URL of custom profile image, or null/undefined for default */
  imageUrl?: string | null;
  /** Size variant: 'sm' (24px) | 'md' (32px) | 'lg' (64px) - default 'md' */
  size?: 'sm' | 'md' | 'lg';
  /** Optional extra classes (e.g. ring/border for the dashboard child cards) */
  className?: string;
}

/**
 * ProfileIcon - Displays profile image or default student avatar
 * Automatically falls back to default icon on image load error
 */
export function ProfileIcon({ imageUrl, size = 'md', className }: ProfileIconProps) {
  const [hasError, setHasError] = useState(false);

  // Reset error state when imageUrl changes (allows retry on new upload)
  useEffect(() => {
    setHasError(false);
  }, [imageUrl]);

  // Determine which image source to use
  const imgSrc = imageUrl && !hasError ? imageUrl : studentAvatar;

  const sizeClasses = {
    sm: 'w-6 h-6',
    md: 'w-8 h-8',
    lg: 'w-16 h-16',
  };

  return (
    <img
      src={imgSrc}
      alt="Profile"
      onError={() => setHasError(true)}
      className={cn('rounded-full object-cover flex-shrink-0', sizeClasses[size], className)}
      aria-label="Student profile icon"
    />
  );
}
